import React, {useCallback, useEffect, useState} from 'react'
import {Col, DangerAlert, H5, Row, SuccessAlert, ButtonLink, PreviousIcon} from '@modifi/ui'
import {Form} from '@modifi/plugin-forms'
import {CustomerUploadManager} from '@modifi/plugin-documents'
import {useTranslation} from '@modifi/plugin-i18n'
import {generatePath} from '@modifi/router'
import {BeneficialOwnerFormData} from '../constants'
import {ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS} from '../../../../routes/constants'
import PersonFormFooter from '../../components/person-form-footer'

import BeneficialOwnersFormContent from '../components/beneficial-owner-form-content'
import {FormContentWrapper} from '../../../../ui/form'
import {EntityFormData} from '../../../../types/common'
import {CompanyUnlockRefresher} from '../../../../plugins/onboarding/components/company-unlock-refresher'
import {aFetchBeneficialOwners} from '../../../../data/beneficial-owners'
import {aFetchDocumentsByExternalPersonId} from '../../../../data/person-documents'

export type ExistingBeneficialOwnerProps = {
  isLoading: boolean
  updatePending: boolean
  initialize: (externalPersonId: string) => void
  externalPersonId: string
  formData: EntityFormData<BeneficialOwnerFormData>
  onSubmit: (changes: Record<string, unknown>, externalPersonId: string) => void
  onDiscard: () => void
  hasError: boolean
  errorMessage?: string
  redirect: (route: string) => void
  isValidPersonId: boolean
  pageIsVerified: boolean
  uboName: string
}

const ExistingBeneficialOwner: React.FC<ExistingBeneficialOwnerProps> = ({
  updatePending,
  externalPersonId,
  hasError,
  errorMessage,
  isLoading,
  onSubmit,
  initialize,
  formData,
  onDiscard,
  redirect,
  isValidPersonId,
  pageIsVerified,
  uboName,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [currentPage, setCurrentPage] = useState(1)

  useEffect(() => {
    initialize(externalPersonId)
  }, [initialize, externalPersonId])

  useEffect(() => {
    onDiscard()
  }, [onDiscard])

  useEffect(() => {
    if (!isValidPersonId) {
      redirect(generatePath(ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS))
    }
  }, [isValidPersonId, redirect])

  const handleSubmit = useCallback(
    (changes: Record<string, unknown>) => {
      if (currentPage === 1) {
        setCurrentPage(currentPage + 1)
      } else {
        onSubmit(changes, externalPersonId)
      }
    },
    [onSubmit, externalPersonId, currentPage]
  )

  const getSubmitButtonLabel = useCallback(() => {
    if (currentPage === 1) {
      return 'Next'
    }
    return t(`sub-section-footer.cta-submit`)
  }, [currentPage, t])

  const handleBackButtonClick = useCallback(() => {
    setCurrentPage(currentPage - 1)
  }, [currentPage])

  const handleSelectPersonAsDataSource = useCallback(() => null, [])

  if (isLoading) {
    return null
  }
  return (
    <CustomerUploadManager>
      {pageIsVerified && (
        <SuccessAlert>{t('ultimate-beneficial-owner-page.fully-verified-alert')}</SuccessAlert>
      )}
      {hasError && errorMessage && <DangerAlert>{errorMessage}</DangerAlert>}
      <Row>
        <Col>
          <ButtonLink
            data-target="ubo-summary-page"
            onClick={() => redirect(ROUTE_COMPANY_PROFILE_BENEFICIAL_OWNERS)}
          >
            <PreviousIcon /> {t('summary-page.back-button.label')}
          </ButtonLink>
        </Col>
      </Row>

      <Row>
        <Col mb={40}>
          <H5>{uboName}</H5>
        </Col>
      </Row>

      <Form
        isLoading={updatePending}
        // @ts-expect-error the forms plugin types these as the DOM FormData
        defaultValues={formData.defaultValues}
        // @ts-expect-error the forms plugin types these as the DOM FormData
        initialValues={formData.initialValues}
        onSubmit={handleSubmit}
        onDiscard={onDiscard}
      >
        <FormContentWrapper>
          <BeneficialOwnersFormContent
            t={t}
            selectPersonAsDataSource={handleSelectPersonAsDataSource}
            dataSourceOptions={[]}
            currentPage={currentPage}
            hasAnyRoleFieldItem
          />
        </FormContentWrapper>

        <CompanyUnlockRefresher
          refetch={(): any[] => [
            aFetchBeneficialOwners(),
            aFetchDocumentsByExternalPersonId(externalPersonId),
          ]}
          initialValues={formData.initialValues}
          defaultValues={formData.defaultValues}
        />
        <PersonFormFooter
          loading={updatePending}
          hasError={hasError}
          submitLabel={getSubmitButtonLabel()}
          canMoveBack={currentPage !== 1}
          handleBackButtonClick={handleBackButtonClick}
          isFinalStep={currentPage === 2}
        />
      </Form>
    </CustomerUploadManager>
  )
}

export default ExistingBeneficialOwner
